/**
 * PWA Installation Utilities
 *
 * Handles PWA install prompts and iOS instructions
 */

import { isRunningAsApp } from './appMode';

// Store the deferred prompt event
let deferredPrompt: any = null;

// Store listeners that need to be notified when install becomes available
const installAvailableListeners: Array<() => void> = [];

/**
 * Subscribe to install availability changes
 */
export function onInstallAvailableChange(callback: () => void): () => void {
  installAvailableListeners.push(callback);
  // Return unsubscribe function
  return () => {
    const index = installAvailableListeners.indexOf(callback);
    if (index > -1) {
      installAvailableListeners.splice(index, 1);
    }
  };
}

/**
 * Initialize PWA install listener
 * Call this once when the app starts
 */
export function initPWAInstall(): void {
  // Listen for the beforeinstallprompt event
  window.addEventListener('beforeinstallprompt', (e) => {
    // Prevent the mini-infobar from appearing on mobile
    e.preventDefault();
    // Stash the event so it can be triggered later
    deferredPrompt = e;
    // Notify all listeners
    installAvailableListeners.forEach(listener => listener());
  });

  // Clear the prompt if app gets installed
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    // Notify all listeners
    installAvailableListeners.forEach(listener => listener());
  });
}

/**
 * Check if PWA install prompt is available
 */
export function canInstallPWA(): boolean {
  // Can't install if already running as app
  if (isRunningAsApp()) {
    return false;
  }

  // Check if we have a deferred prompt (Chrome/Edge/Android)
  if (deferredPrompt) {
    return true;
  }

  // iOS Safari doesn't support beforeinstallprompt
  // Check if it's iOS Safari
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isInStandaloneMode = ('standalone' in window.navigator) && (window.navigator as any).standalone;

  if (isIOS && !isInStandaloneMode) {
    return true;
  }

  return false;
}

/**
 * Check if device is iOS
 */
export function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

/**
 * Show the PWA install prompt
 * Returns true if successfully shown, false otherwise
 */
export async function showInstallPrompt(): Promise<boolean> {
  // If iOS, can't show native prompt
  if (isIOS()) {
    return false;
  }

  // If no deferred prompt, can't install
  if (!deferredPrompt) {
    return false;
  }

  try {
    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;

    // Clear the deferred prompt
    deferredPrompt = null;

    return outcome === 'accepted';
  } catch (error) {
    console.error('Error showing install prompt:', error);
    return false;
  }
}

/**
 * Check if install banner should be shown
 * Based on localStorage dismissal
 */
export function shouldShowInstallBanner(): boolean {
  // Don't show if already in app mode
  if (isRunningAsApp()) {
    return false;
  }

  // Don't show if can't install
  if (!canInstallPWA()) {
    return false;
  }

  // Check if user dismissed it
  try {
    const dismissed = localStorage.getItem('pwa_install_banner_dismissed');
    if (dismissed === 'true') {
      return false;
    }
  } catch {
    // localStorage not available
  }

  return true;
}

/**
 * Mark install banner as dismissed
 */
export function dismissInstallBanner(): void {
  try {
    localStorage.setItem('pwa_install_banner_dismissed', 'true');
  } catch {
    // localStorage not available
  }
}
